import { ImageViewer } from "@/components/ImageViewer";
import { useEffect, useRef, useState } from "react";
import { Volume2, Square, Loader2, PenTool, Lock, Layers, Download } from "lucide-react";
import { toast } from "sonner";
import { API, http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { logWarn } from "@/lib/log";
import { useAiJob, isStopped, stoppedToast } from "@/lib/aiJob";

function Diagram({ id }) {
  const [d, setD] = useState({ status: "pending" });
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let stop = false;
    let t;
    const poll = async () => {
      try {
        const { data } = await http.get(`/diagrams/${id}`);
        if (stop) return;
        setD(data);
        if (data.status === "done") {
          const r = await http.get(`/diagrams/${id}/image`, { responseType: "blob" });
          if (!stop) setSrc(URL.createObjectURL(r.data));
        } else if (data.status === "pending") t = setTimeout(poll, 3000);
      } catch (e) {
        logWarn("Diagram load failed", e);
        if (!stop) setD({ status: "failed", error: "Couldn't load diagram" });
      }
    };
    poll();
    return () => { stop = true; clearTimeout(t); };
  }, [id]);
  const [open, setOpen] = useState(false);
  const { refreshEnt } = useAuth();
  // Stopping an image before it finishes is always free.
  const cancel = async () => {
    setD({ status: "canceled", error: "Stopped. No credits were used." });
    try { const { data } = await http.post(`/diagrams/${id}/cancel`); if (data.status === "canceled") stoppedToast(0); else setD({ status: "pending" }); }
    catch (e) { logWarn("Stop image failed", e); } finally { refreshEnt(); }
  };
  if (d.status === "canceled") return <p className="text-xs text-neutral-500" data-testid={`diagram-canceled-${id}`}>{d.error || "Stopped. No credits were used."}</p>;
  if (d.status === "failed") return <p className="text-xs text-red-300" data-testid={`diagram-failed-${id}`}>{d.error}</p>;
  if (!src) return (
    <div className="h-56 w-full max-w-md rounded-xl border border-white/10 bg-white/[0.03] flex flex-col items-center justify-center gap-3 text-sm text-neutral-500" data-testid={`diagram-pending-${id}`}>
      <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Drawing your image…</span>
      {d.status === "pending" && (
        <button onClick={cancel} data-testid={`diagram-stop-${id}`}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 text-xs text-neutral-300 hover:bg-white/10 transition-colors">
          <Square className="h-3 w-3 fill-current" /> Stop
        </button>
      )}
    </div>
  );
  return (
    <div className="max-w-md w-full">
      <button type="button" onClick={() => setOpen(true)} className="block w-full" aria-label="Open image" data-testid={`diagram-open-${id}`}>
        <img src={src} alt="Generated image" decoding="async" className="rounded-xl border border-white/10 w-full max-h-[70vh] object-contain bg-white rise" data-testid={`diagram-image-${id}`} />
      </button>
      <a href={src} download={`brynai-${id.slice(0, 8)}.png`} className="mt-1.5 inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white" data-testid={`diagram-download-${id}`}>
        <Download className="h-3 w-3" /> Download
      </a>
      <ImageViewer src={open ? src : null} filename={`brynai-${id.slice(0, 8)}.png`} onClose={() => setOpen(false)} />
    </div>
  );
}

let currentAudio = null;
export const DAILY_NOTE = "Daily limit reached, resets tomorrow";

const nextUtcMidnight = () => { const d = new Date(); d.setUTCHours(24, 0, 0, 0); return d; };
const fmtLeft = (ms) => { const m = Math.max(0, Math.ceil(ms / 60000)); return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`; };

export function useResetCountdown() {
  const [label, setLabel] = useState(() => fmtLeft(nextUtcMidnight() - Date.now()));
  useEffect(() => {
    const t = setInterval(() => setLabel(fmtLeft(nextUtcMidnight() - Date.now())), 30000);
    return () => clearInterval(t);
  }, []);
  return `resets in ${label}`;
}

export function useDaily(kind) {
  const { ent } = useAuth();
  const d = ent?.daily?.[kind];
  if (!d) return { hint: null, blocked: false, left: null };
  return { hint: d.left > 0 ? `${d.left} left today` : DAILY_NOTE, blocked: d.left < 1, left: d.left };
}

function BlockedHint({ testid }) {
  const reset = useResetCountdown();
  return <span className="text-[10px] text-yellow-200/80" data-testid={testid}>Daily limit reached, {reset}</span>;
}

export function DailyHint({ kind, testid }) {
  const { hint, blocked } = useDaily(kind);
  if (!hint) return null;
  if (blocked) return <BlockedHint testid={testid} />;
  return <span className="text-[10px] text-neutral-500" data-testid={testid}>{hint}</span>;
}

export async function speechError(res) {
  const detail = (await res.json().catch(() => ({}))).detail;
  if (res.status === 402) window.dispatchEvent(new CustomEvent("paywall", { detail }));
  return new Error(typeof detail === "string" ? detail : detail?.message || "Couldn't read this aloud");
}

export function ListenButton({ messageId, cached }) {
  const { refreshEnt } = useAuth();
  const { blocked } = useDaily("voice");
  const [state, setState] = useState("idle");
  const [played, setPlayed] = useState(!!cached);
  const audioRef = useRef(null);
  const job = useAiJob({ onStopped: refreshEnt });
  useEffect(() => () => audioRef.current?.pause(), []);
  const toggle = async () => {
    if (state === "playing") { audioRef.current?.pause(); setState("idle"); return; }
    if (state === "loading") { job.stop(); setState("idle"); return; }
    if (blocked && !played) { toast.error(DAILY_NOTE); return; }
    setState("loading");
    try {
      const res = await job.run(({ headers, signal }) => fetch(`${API}/messages/${messageId}/speech`, { method: "POST", credentials: "include", headers, signal }));
      if (!played) refreshEnt();
      if (res.status === 499) { setState("idle"); return; }
      if (!res.ok) throw await speechError(res);
      setPlayed(true);
      const audio = new Audio(URL.createObjectURL(await res.blob()));
      currentAudio?.pause();
      currentAudio = audio;
      audioRef.current = audio;
      audio.onended = audio.onpause = () => setState("idle");
      await audio.play();
      setState("playing");
    } catch (e) { if (!isStopped(e)) toast.error(e.message); setState("idle"); }
  };
  const Icon = state === "playing" ? Square : state === "loading" ? Loader2 : Volume2;
  return (
    <button onClick={toggle} data-testid={`listen-btn-${messageId}`}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/10 text-neutral-400 hover:bg-sky-500/10 hover:text-sky-300 transition-colors">
      <Icon className={`h-3 w-3 ${state === "loading" ? "animate-spin" : ""}`} /> {state === "idle" ? "Listen" : "Stop"}
      {!played && <DailyHint kind="voice" testid={`listen-daily-${messageId}`} />}
    </button>
  );
}

export function DrawButton({ msg, onDrawn }) {
  const { ent, refreshEnt } = useAuth();
  const { blocked } = useDaily("draw");
  const [busy, setBusy] = useState(false);
  const locked = ent && !ent.features.draw;
  const draw = async () => {
    if (locked) { window.dispatchEvent(new CustomEvent("paywall", { detail: { code: "feature", feature: "draw", needs: "plus" } })); return; }
    if (blocked) { toast.error(DAILY_NOTE); return; }
    setBusy(true);
    try {
      const { data } = await http.post(`/messages/${msg.message_id}/diagram`);
      onDrawn(data.diagram_id);
    } catch (e) { if (e.response?.status !== 402) toast.error(formatError(e)); } finally { setBusy(false); refreshEnt(); }
  };
  return (
    <button onClick={draw} disabled={busy} data-testid={`draw-btn-${msg.message_id}`}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/10 text-neutral-400 hover:bg-violet-500/10 hover:text-violet-300 transition-colors">
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <PenTool className="h-3 w-3" />} Draw it
      {locked ? <span className="flex items-center gap-0.5 text-[10px] text-neutral-500"><Lock className="h-2.5 w-2.5" />Plus</span>
        : <DailyHint kind="draw" testid={`draw-daily-${msg.message_id}`} />}
    </button>
  );
}

export function FlashcardButton({ msg }) {
  const { ent } = useAuth();
  const [state, setState] = useState("idle");
  const job = useAiJob();
  const locked = ent && !ent.features.practice;
  const make = async () => {
    if (state === "busy") { job.stop(); setState("idle"); return; }
    if (locked) { window.dispatchEvent(new CustomEvent("paywall", { detail: { code: "feature", feature: "flashcards", needs: "starter" } })); return; }
    setState("busy");
    try { await job.run((o) => http.post(`/messages/${msg.message_id}/flashcard`, null, o)); setState("done"); toast.success("Flashcard added to Practice"); }
    catch (e) { if (!isStopped(e) && e.response?.status !== 402) toast.error(formatError(e)); setState((s) => (s === "busy" ? "idle" : s)); }
  };
  return (
    <button onClick={make} disabled={state === "done"} data-testid={`flashcard-btn-${msg.message_id}`}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/10 text-neutral-400 hover:bg-emerald-500/10 hover:text-emerald-300 transition-colors disabled:opacity-60">
      {state === "busy" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Layers className="h-3 w-3" />} {state === "done" ? "Card made" : state === "busy" ? "Stop" : "Make flashcard"}
      {locked && <span className="flex items-center gap-0.5 text-[10px] text-neutral-500"><Lock className="h-2.5 w-2.5" />Starter</span>}
    </button>
  );
}

export function Diagrams({ ids }) {
  if (!ids?.length) return null;
  return <div className="mt-4 space-y-3" data-testid="message-diagrams">{ids.map((id) => <Diagram key={id} id={id} />)}</div>;
}
